import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, Calendar, Calculator, ShieldCheck, BookCheck, MessageSquare,
  User as UserIcon, PlusSquare, ClipboardList, Users, LogOut, Menu, X, Bell, Inbox, Settings,
} from "lucide-react";
import TritalLogo from "../TritalLogo";
import PageMeta from "../seo/PageMeta";
import { useAuth } from "../../context/AuthContext";

function menuForRole(role) {
  const common = [
    { to: "/dashboard/home", label: "Overview", icon: LayoutDashboard },
    { to: "/dashboard/messages", label: "Messages", icon: MessageSquare },
    { to: "/dashboard/profile", label: "Profile", icon: UserIcon },
  ];
  if (role === "clinician") {
    return [
      common[0],
      { to: "/dashboard/shifts", label: "PRN Shifts", icon: Calendar },
      { to: "/dashboard/jobs", label: "Jobs", icon: Briefcase },
      { to: "/dashboard/calculator", label: "Travel Calc", icon: Calculator },
      { to: "/dashboard/credentials", label: "Credentials", icon: ShieldCheck },
      { to: "/dashboard/skills", label: "Skills", icon: BookCheck },
      common[1], common[2],
    ];
  }
  if (role === "facility") {
    return [
      common[0],
      { to: "/dashboard/facility", label: "Operations", icon: Settings },
      { to: "/dashboard/post-shift", label: "Post Shift", icon: PlusSquare },
      { to: "/dashboard/post-job", label: "Post Job", icon: PlusSquare },
      { to: "/dashboard/manage-shifts", label: "Manage Shifts", icon: ClipboardList },
      { to: "/dashboard/directory", label: "Clinicians", icon: Users },
      { to: "/dashboard/credentials", label: "Credentials", icon: ShieldCheck },
      common[1], common[2],
    ];
  }
  if (role === "recruiter") {
    return [
      common[0],
      { to: "/dashboard/recruiter", label: "Pipeline", icon: Inbox },
      { to: "/dashboard/directory", label: "Clinicians", icon: Users },
      { to: "/dashboard/credentials", label: "Credentials", icon: ShieldCheck },
      { to: "/dashboard/post-job", label: "Post Job", icon: PlusSquare },
      { to: "/dashboard/jobs", label: "Jobs", icon: Briefcase },
      common[1], common[2],
    ];
  }
  // admin
  return [
    common[0],
    { to: "/dashboard/admin", label: "Admin Panel", icon: LayoutDashboard },
    { to: "/dashboard/directory", label: "Clinicians", icon: Users },
    { to: "/dashboard/credentials", label: "Credentials", icon: ShieldCheck },
    { to: "/dashboard/shifts", label: "All Shifts", icon: Calendar },
    { to: "/dashboard/jobs", label: "All Jobs", icon: Briefcase },
    common[1], common[2],
  ];
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = menuForRole(user?.role);

  const doLogout = () => { logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* All dashboard routes are auth-walled — never indexed */}
      <PageMeta title="Dashboard | TRITAL Care®" noIndex />
      {/* Sidebar */}
      <aside className={`fixed z-40 inset-y-0 left-0 w-72 bg-trital-navy-ink text-white transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="dashboard-sidebar">
        <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
          <TritalLogo variant="dark" />
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-white/70"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3">
          <div className="px-3 py-4 mb-3 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[11px] uppercase tracking-widest text-white/50">Signed in as</div>
            <div className="font-display font-bold text-white truncate">{user?.name}</div>
            <div className="mt-1 inline-flex items-center gap-2 text-[11px] px-2 py-0.5 rounded-full bg-trital-red/15 text-trital-red border border-trital-red/30 uppercase tracking-wider">{user?.role}</div>
          </div>
          <nav className="space-y-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                      isActive ? "bg-white text-trital-navy" : "text-white/80 hover:bg-white/10 hover:text-white"
                    }`
                  }
                  data-testid={`side-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-[18px] h-[18px]" /> {it.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
        <div className="absolute bottom-4 left-3 right-3">
          <button onClick={doLogout} className="w-full flex items-center gap-2 justify-center px-3 py-2.5 rounded-lg bg-trital-red hover:bg-trital-red-deep text-white font-semibold text-sm" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Topbar + content */}
      <div className="lg:pl-72">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-5">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden" data-testid="sidebar-open"><Menu className="w-5 h-5" /></button>
          <div className="hidden lg:block">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">TRITAL Care Workforce</div>
            <div className="font-display font-bold text-slate-900 text-lg">Operations Console</div>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 rounded-full hover:bg-slate-100" data-testid="notifications-btn">
              <Bell className="w-5 h-5 text-slate-700" />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-trital-red" />
            </button>
            <div className="w-9 h-9 rounded-full bg-trital-navy text-white font-display font-bold flex items-center justify-center">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
